import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IconArrowLeft as ArrowLeft, IconChevronRight as ChevronRight } from '@tabler/icons-react';
import { 
  Avatar, 
  Button, 
  Input, 
  Separator, 
  toast 
} from '@heroui/react';
import { trpc, trpcClient } from '@/lib/trpc';
import { useAppSelector, useAppDispatch } from '@/stores/hooks';
import { updateUserInfo } from '@his/shared/stores/slices/authSlice';
import { AvatarSelectDrawer } from './components/AvatarSelectDrawer';
import { AvatarEditor } from './components/AvatarEditor';
import { apiClient } from '@his/shared/utils/api-client';
import defaultAvatar from '@/assets/images/default-avatar.webp';
import { navigateBackWithFallback } from '@/lib/navigation';

/**
 * 编辑资料页面
 * 
 * 特性：
 * - ✅ 全屏组件，遮盖住 tabBar
 * - ✅ 从 auth 缓存中载入用户个人信息
 * - ✅ 头像显示逻辑同我的页面，点击头像行调用已开发的头像编辑组件
 * - ✅ 其他项采用 Input 组件，去除边框
 * - ✅ 调用用户模块 update 接口更新用户信息
 */
export default function EditProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [isBindingWechat, setIsBindingWechat] = useState(false);
  const processedWechatCodeRef = useRef<string | null>(null);

  // 表单状态
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [realName, setRealName] = useState(user?.realname || '');
  const [mobile, setMobile] = useState(user?.mobile || '');
  const [email, setEmail] = useState(user?.email || '');

  // 同步用户信息到表单状态（当 user 更新时）
  useEffect(() => {
    if (user) {
      setNickname(user.nickname || '');
      setRealName(user.realname || '');
      setMobile(user.mobile || '');
      setEmail(user.email || '');
    }
  }, [user]);

  // 头像编辑相关状态
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editorImageSrc, setEditorImageSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // 更新个人资料（走 auth 模块，不依赖用户管理权限）
  const updateMyProfileMutation = trpc.auth.updateMyProfile.useMutation();
  const bindWechatMutation = trpc.auth.bindWechat.useMutation();
  // 刷新用户信息query
  const refreshUserInfoQuery = trpc.auth.refreshUserInfo.useQuery(undefined, {
    enabled: false,
  });

  // 在 /me/edit 自身完成微信授权回调处理（code 在 query 中）
  useEffect(() => {
    const code = searchParams.get('code');
    if (!code || processedWechatCodeRef.current === code) {
      return;
    }
    processedWechatCodeRef.current = code;
    let cancelled = false;

    (async () => {
      try {
        setIsBindingWechat(true);
        await bindWechatMutation.mutateAsync({ code });
        if (cancelled) return;

        const refreshed = await refreshUserInfoQuery.refetch();
        if (refreshed.data) {
          const userData = refreshed.data as any;
          dispatch(
            updateUserInfo({
              user: {
                id: userData.id,
                username: userData.username,
                nickname: userData.nickname ?? undefined,
                realname: userData.realname ?? undefined,
                email: userData.email ?? undefined,
                mobile: userData.mobile ?? undefined,
                avatarUrl: userData.avatarUrl ?? undefined,
                status: userData.status ?? undefined,
                type: userData.type ?? undefined,
                tenantId: userData.tenantId ?? undefined,
                permissions: userData.permissions ?? undefined,
                roles: userData.roles ?? undefined,
                apps: userData.apps ?? undefined,
                spaces: userData.spaces ?? undefined,
              },
            })
          );
        }

        toast.success('微信账号已绑定');
      } catch (e) {
        if (cancelled) return;
        toast.danger(e instanceof Error ? e.message : '绑定失败，请重试');
      } finally {
        if (!cancelled) {
          setIsBindingWechat(false);
          // 清理 OAuth query，避免重复执行并减少无效历史痕迹
          navigate('/me/edit', {
            replace: true,
            state: location.state ?? null,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在 URL code 变化时执行
  }, [searchParams]);

  // 获取头像URL，如果为空则使用默认头像
  const avatarUrl = user?.avatarUrl?.trim() ? user.avatarUrl : defaultAvatar;

  // 处理文件选择
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      toast.danger('请选择图片文件');
      return;
    }

    // 创建预览URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        setEditorImageSrc(result);
      }
    };
    reader.readAsDataURL(file);

    // 清空input，以便下次选择同一文件时也能触发change事件
    event.target.value = '';
  };

  // 从相册选择
  const handleSelectFromAlbum = () => {
    fileInputRef.current?.click();
  };

  // 拍照
  const handleTakePhoto = () => {
    cameraInputRef.current?.click();
  };

  // 完成头像编辑并上传
  const handleAvatarComplete = async (imageFile: File) => {
    if (!user?.id) {
      toast.danger('用户信息不存在');
      setEditorImageSrc(null);
      return;
    }

    /** HeroUI v3：`toast()` 返回 `string`，`toast.close` 仅接受 `string` */
    let loadingKey: string | undefined;
    try {
      loadingKey = toast('正在上传并更新头像…', { isLoading: true, timeout: 0 });

      const uploadResult = await apiClient.uploadFile({
        file: imageFile,
        description: '用户头像',
        urlType: 'permanent',
      });

      await updateMyProfileMutation.mutateAsync({
        avatarUrl: uploadResult.url,
      });

      // 刷新用户信息
      const refreshedUser = await refreshUserInfoQuery.refetch();
      if (refreshedUser.data) {
        const userData = refreshedUser.data as any;
        dispatch(
          updateUserInfo({
            user: {
              id: userData.id,
              username: userData.username,
              nickname: userData.nickname ?? undefined,
              realname: userData.realname ?? undefined,
              email: userData.email ?? undefined,
              mobile: userData.mobile ?? undefined,
              avatarUrl: userData.avatarUrl ?? undefined,
              status: userData.status ?? undefined,
              type: userData.type ?? undefined,
              tenantId: userData.tenantId ?? undefined,
              permissions: userData.permissions ?? undefined,
              roles: userData.roles ?? undefined,
              apps: userData.apps ?? undefined,
              spaces: userData.spaces ?? undefined,
            },
          })
        );
      }

      if (loadingKey !== undefined) {
        toast.close(loadingKey);
      }
      toast.success('头像更新成功');
      setEditorImageSrc(null);
    } catch (error) {
      console.error('头像更新失败:', error);
      if (loadingKey !== undefined) {
        toast.close(loadingKey);
      }
      const msg =
        error instanceof Error
          ? error.message
          : typeof error === 'object' &&
              error !== null &&
              'message' in error &&
              typeof (error as { message: unknown }).message === 'string'
            ? (error as { message: string }).message
            : '头像更新失败';
      toast.danger(msg);
    }
  };

  // 取消头像编辑
  const handleAvatarCancel = () => {
    setEditorImageSrc(null);
  };

  // 保存用户信息
  const handleSave = async () => {
    if (!user?.id) {
      toast.danger('用户信息不存在');
      return;
    }

    let loadingKey: string | undefined;
    try {
      loadingKey = toast('正在保存…', { isLoading: true, timeout: 0 });

      await updateMyProfileMutation.mutateAsync({
        nickname: nickname.trim() || undefined,
        realname: realName.trim() || undefined,
        mobile: mobile.trim() || undefined,
        email: email.trim() || undefined,
      });

      // 刷新用户信息
      const refreshedUser = await refreshUserInfoQuery.refetch();
      if (refreshedUser.data) {
        const userData = refreshedUser.data as any;
        dispatch(
          updateUserInfo({
            user: {
              id: userData.id,
              username: userData.username,
              nickname: userData.nickname ?? undefined,
              realname: userData.realname ?? undefined,
              email: userData.email ?? undefined,
              mobile: userData.mobile ?? undefined,
              avatarUrl: userData.avatarUrl ?? undefined,
              status: userData.status ?? undefined,
              type: userData.type ?? undefined,
              tenantId: userData.tenantId ?? undefined,
              permissions: userData.permissions ?? undefined,
              roles: userData.roles ?? undefined,
              apps: userData.apps ?? undefined,
              spaces: userData.spaces ?? undefined,
            },
          })
        );
      }

      if (loadingKey !== undefined) {
        toast.close(loadingKey);
      }
      toast.success('保存成功');
      navigateBackWithFallback(navigate, location, '/me');
    } catch (error) {
      console.error('保存失败:', error);
      if (loadingKey !== undefined) {
        toast.close(loadingKey);
      }
      toast.danger(error instanceof Error ? error.message : '保存失败');
    }
  };

  return (
    <>
      {/* 全屏容器，遮盖 tabBar；z-40 低于 Drawer(z-50) 以便头像选择抽屉能浮在上层 */}
      <div className="fixed inset-0 z-40 flex flex-col bg-background">
        {/* 顶部导航栏：左右按钮 shrink-0 防止在 iOS 微信内被挤出视口（安全区由浏览器/微信内置浏览器处理） */}
        <div className="flex items-center justify-between gap-2 px-4 h-12 shrink-0 border-b">
          <Button
            variant="ghost"
            isIconOnly
            onPress={() => navigateBackWithFallback(navigate, location, '/me')}
            className="h-8 w-8 shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold flex-1 min-w-0 truncate text-center">
            编辑资料
          </h1>
          <Button
            variant="primary"
            size="sm"
            onPress={handleSave}
            className="shrink-0 min-w-[4rem]"
          >
            保存
          </Button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto">
          {/* 头像行 */}
          <div
            className="flex items-center justify-between px-4 py-4 cursor-pointer hover:bg-default active:bg-default transition-colors"
            onClick={() => setDrawerOpen(true)}
          >
            <span className="text-base text-foreground">头像</span>
            <div className="flex items-center gap-2">
              <Avatar className="h-12 w-12">
                <Avatar.Image src={avatarUrl} alt="头像" />
                <Avatar.Fallback className="bg-default-100">
                  <img src={defaultAvatar} alt="默认头像" className="w-full h-full object-cover" />
                </Avatar.Fallback>
              </Avatar>
              <ChevronRight className="h-4 w-4 text-default-500" />
            </div>
          </div>

          <Separator />

          {/* 用户名 */}
          <div className="flex items-center justify-between px-4 py-4">
            <span className="text-base text-foreground">用户名</span>
            <span className="text-base text-default-500">{user?.username || '-'}</span>
          </div>

          <Separator />

          {/* 昵称 */}
          <div className="flex items-center justify-between px-4 py-4">
            <span className="text-base text-foreground shrink-0">昵称</span>
            <Input
              variant="secondary"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="请输入昵称"
              className="flex-1 max-w-[200px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent text-right"
            />
          </div>

          <Separator />

          {/* 真实姓名 */}
          <div className="flex items-center justify-between px-4 py-4">
            <span className="text-base text-foreground shrink-0">真实姓名</span>
            <Input
              variant="secondary"
              value={realName}
              onChange={(e) => setRealName(e.target.value)}
              placeholder="请输入真实姓名"
              className="flex-1 max-w-[200px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent text-right"
            />
          </div>

          <Separator />

          {/* 手机号 */}
          <div className="flex items-center justify-between px-4 py-4">
            <span className="text-base text-foreground shrink-0">手机号</span>
            <Input
              variant="secondary"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="请输入手机号"
              type="tel"
              className="flex-1 max-w-[200px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-right bg-transparent border-none"
            />
          </div>

          <Separator />

          <Separator />

          {/* 邮箱 */}
          <div className="flex items-center justify-between px-4 py-4">
            <span className="text-base text-foreground shrink-0">邮箱</span>
            <Input
              variant="secondary"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="请输入邮箱"
              type="email"
              className="flex-1 max-w-[200px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent text-right"
            />
          </div>

          <Separator />

          {/* 绑定微信账号：需在微信内打开 H5 才能完成授权绑定 */}
          <div
            className={`flex items-center justify-between px-4 py-4 transition-colors ${(user as { wechatBound?: boolean } | undefined)?.wechatBound ? '' : 'cursor-pointer hover:bg-default active:bg-default'}`}
            onClick={async () => {
              const wechatBound = (user as { wechatBound?: boolean } | undefined)?.wechatBound;
              if (wechatBound || isBindingWechat) return;
              try {
                const redirectUri = `${window.location.origin}/me/edit`;
                const { auth_url } = await trpcClient.wechat.getAuthUrl.query({
                  redirect_uri: redirectUri,
                  scope: 'snsapi_userinfo',
                });
                // 使用 replace 减少无效历史记录，降低微信底部路由工具条触发概率
                window.location.replace(auth_url);
              } catch (e) {
                toast.danger(e instanceof Error ? e.message : '获取授权链接失败');
              }
            }}
          >
            <span className="text-base text-foreground">绑定微信账号</span>
            <div className="flex items-center gap-2">
              <span className="text-base text-default-500">
                {(user as { wechatBound?: boolean } | undefined)?.wechatBound
                  ? '已绑定'
                  : isBindingWechat
                    ? '绑定中...'
                    : '点击绑定'}
              </span>
              {!(user as { wechatBound?: boolean } | undefined)?.wechatBound && (
                <ChevronRight className="h-4 w-4 text-default-500" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* 头像选择抽屉 */}
      <AvatarSelectDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onSelectFromAlbum={handleSelectFromAlbum}
        onTakePhoto={handleTakePhoto}
      />

      {/* 头像编辑界面 */}
      {editorImageSrc && (
        <AvatarEditor
          imageSrc={editorImageSrc}
          onCancel={handleAvatarCancel}
          onComplete={handleAvatarComplete}
        />
      )}
    </>
  );
}
